import { useState, useEffect, lazy, Suspense, ReactNode } from 'react'
import styled, { createGlobalStyle, css } from 'styled-components'
import { useQuery } from '@apollo/client'
import { sortBy, union, isEmpty, isBoolean } from 'lodash-es'
import Tribute from 'tributejs'
import 'tributejs/dist/tribute.css'
import { useTranslation } from 'react-i18next'

import { FROALA_STYLES } from '@/graphql/queries/FroalaStyle'
import { Form, Loader } from '@/ui'
import Text from '@/ui/Text'
import Icon from '@/ui/Icon'
import config from '@/config'
import { useLoggedInUser } from '@/hooks'
import parseVariables from '@/components/Field/Editor/parse-variables'
import { FroalaFontFamilies } from './Fonts'
import froalaCustomSetup from './froala-custom-setup'
import ErrorLabel from '../ErrorLabel'

interface FroalaGlobalStyleProps {
  toggled?: boolean
  toolbarSticky?: boolean
  toolbarStickyOffset?: number
}

interface EditorProps {
  input: any
  onChange?: (value?: string | null) => void
  label?: ReactNode
  touched?: boolean
  error?: string
  sensitive?: boolean
  disabled?: boolean
  saving?: boolean
  togglable?: boolean
  disableToggling?: boolean
  preserveAllWhitespace?: boolean
  customFroalaConfig?: any
  variables?: any
  onBlur?: (value?: string | null) => void
  placeholders?: any
  excludeToolbarButtons?: any
}

interface DisabledWrapperProps {
  disabled?: boolean
}

interface EditorWrapperProps {
  saving?: boolean
  savingText?: string
  error?: boolean
}

const FroalaEditorComponent = lazy(() => import('react-froala-wysiwyg'))
let FroalaEditor: any = undefined

const FroalaGlobalStyle = createGlobalStyle<FroalaGlobalStyleProps>`
  ${({ toggled, toolbarSticky, toolbarStickyOffset, theme }) => css`
    table {
      table-layout: fixed;
    }

    .fr-popup {
      z-index: 1000 !important;
    }

    .fr-box.fr-box.fr-box {
      border: 1px solid #bdcbd0 !important;
      border-color: #bdcbd0 !important;
      box-shadow: 0px 2px 12px -5px rgb(0 46 62 / 5%);
      border-radius: 4px;
      background: #fff;
    }

    .fr-toolbar.fr-toolbar.fr-toolbar,
    .fr-wrapper.fr-wrapper.fr-wrapper {
      border: none !important;
      background: none !important;
    }

    .fr-second-toolbar {
      display: none;
    }

    .fr-view {
      padding: 0.67857143em 1em !important;
      font-weight: 300;
      color: ${theme.colors.textGrey};
    }

    .fr-placeholder {
      margin-top: 0 !important;
    }

    // Hide the unlicensed copy text in development

    label {
      opacity: 1 !important;
    }

    ${!toggled &&
    css`
      .fr-toolbar {
        display: none;
      }

      .fr-wrapper {
        border-top: 1px solid #bdcbd0 !important;
      }
    `}

    ${toolbarSticky &&
    css`
      .fr-toolbar.fr-toolbar.fr-toolbar {
        background: #fff !important;
        position: sticky !important;
        z-index: 2;
      }
    `}

    ${toolbarStickyOffset &&
    css`
      .fr-toolbar.fr-toolbar.fr-toolbar {
        top: 50px;
      }
    `}
  `}
`

const EditorContent = ({
  input,
  onChange,
  label,
  touched,
  error,
  sensitive = false,
  disabled = false,
  disableToggling = false,
  saving = false,
  togglable = false,
  preserveAllWhitespace = true,
  customFroalaConfig = {},
  variables = [],
  onBlur,
  placeholders = {},
  excludeToolbarButtons = [],
}: EditorProps) => {
  useEffect(() => {
    if (!FroalaEditor) {
      const importFroala = async () => {
        await Promise.all([
          import('froala-editor/css/froala_style.min.css'),
          import('froala-editor/css/froala_editor.pkgd.min.css'),
          import('froala-editor/js/plugins.pkgd.min.js'),
          import('froala-editor/js/languages/da'),
        ])

        FroalaEditor = (await import(`froala-editor`)).default
      }

      importFroala()
    }
  }, [])

  const { t, i18n } = useTranslation()
  const { companyId } = useLoggedInUser()
  const [initialized, setInitialized] = useState(false)
  const [visible, setVisible] = useState(false)
  const [toggled, setToggled] = useState(!togglable)
  const [froalaCore, setFroalaCore] = useState<any>(null)

  const { data, loading } = useQuery(FROALA_STYLES, {
    variables: {
      companyId,
    },
  })

  froalaCustomSetup(FroalaEditor, { t, placeholders })

  const tribute = isEmpty(placeholders)
    ? new Tribute({
        trigger: '#',
        values: sortBy(variables, 'key'),
        selectTemplate: item =>
          `<span class="ui label fr-deletable fr-tribute" contenteditable="false" data-value="${item.original.value}">${item.original.key}</a></span>`,
      })
    : new Tribute({
        trigger: '#',
        values: Object.entries(placeholders).map(([key, value]) => {
          return {
            key,
            value,
          }
        }),
        // @ts-ignore
        menuItemTemplate: item => item.original.value,
        selectTemplate: item => `{{${item.original.key}}}`,
        lookup: 'value',
        allowSpaces: true,
        replaceTextSuffix: '',
      })

  // Froala can't properly render double spaces when it's being initialized,
  // UNLESS the second space is a &nbsp; entity.
  // Froala already does this sometimes.. But more often than not it doesn't work 🤷
  // Regex hack to the rescue 💩
  // Only run this for the intial value, otherwise it can cause the cursor to jump to the start:
  //  https://app.clickup.com/t/865czepq5
  const parseModel = (value?: string | null) => {
    if (typeof value !== 'string') {
      return ''
    }

    return preserveAllWhitespace ? value?.replace(/ {2}/g, ' &nbsp;') : value
  }

  const froalaConfig = {
    key: 'BWC6D-16A3B3D2E2D1A17vcC-16C7aaF1rH-7A-31hC7D6C5D4D2E3A3C6A6C6==',
    pastePlain: !customFroalaConfig.documentReady,
    toolbarSticky: false,
    toolbarStickyOffset: false,
    pasteDeniedAttrs: !customFroalaConfig.documentReady ? ['style'] : [],
    pasteAllowedStyleProps: [
      'font-size',
      'background',
      'color',
      'width',
      'text-align',
      'vertical-align',
      'background-color',
      'line-height',
    ],
    htmlRemoveTags: ['script', 'base'],
    imageResizeWithPercent: true,
    imageRoundPercent: true,
    attribution: false,
    language: i18n.language,
    imageBrowse: false,
    quickInsertEnabled: false,
    emoticonsUseImage: false,
    imageUploadURL: `${config.endpoints.graphql}/upload_file`,
    imageUploadMethod: 'POST',
    videoUpload: false,
    useClasses: false,
    wordPasteModal: false,
    heightMin: !togglable ? 150 : undefined,
    fontSize: [
      '6',
      '7',
      '8',
      '9',
      '10',
      '11',
      '12',
      '13',
      '14',
      '15',
      '16',
      '17',
      '18',
      '19',
      '20',
      '24',
      '30',
      '36',
      '48',
      '60',
      '72',
      '96',
    ],
    fontFamily: FroalaFontFamilies,
    imageEditButtons: [
      'imageReplace',
      'imageAlign',
      'imageRemove',
      '|',
      'linkOpen',
      'linkEdit',
      'linkRemove',
      '-',
      'imageDisplay',
      'imageStyle',
      'imageSize',
    ],
    shortcutsEnabled: [
      'show',
      'bold',
      'italic',
      'underline',
      'strikeThrough',
      'indent',
      'outdent',
      'undo',
      'redo',
      'insertImage',
    ],
    inlineStyles: (data?.froalaStyles?.edges ?? [])
      .map(({ node }) => node)
      .reduce<Record<string, string>>((acc, item) => {
        if (item) {
          acc[item.name] = `
          ${item.css}
          overflow: hidden;
          max-width: 400px;
        `
        }

        return acc
      }, {}),
    tableStyles: {
      'fr-dashed-borders': t('Dashed Borders'),
      'fr-alternate-rows': t('Alternate Rows'),
      'no-border': t('No border'),
    },
    toolbarButtons: customFroalaConfig.documentReady
      ? union(
          [
            'undo',
            'redo',
            'print',
            'bold',
            'italic',
            'underline',
            'strikeThrough',
            'textColor',
            'backgroundColor',
            'clearFormatting',
            'alignLeft',
            'alignCenter',
            'alignRight',
            'alignJustify',
            'formatOL',
            'formatUL',
            'indent',
            'outdent',
            'paragraphFormat',
            'fontFamily',
            'fontSize',
            'lineHeight',
            'insertLink',
            'insertImage',
            'insertTable',
            'html',
            'specialCharacters',
            'inlineStyle',
          ],
          !isEmpty(placeholders)
            ? [
                'placeholderInfo',
                'placeholderDropdown',
                'insertConditionalPlaceholder',
                'insertInvertedConditionalPlaceholder',
              ]
            : [],
        ).filter(
          toolbarButton => !excludeToolbarButtons.includes(toolbarButton),
        )
      : customFroalaConfig.toolbarButtons ||
        [
          'bold',
          'italic',
          'underline',
          'fontSize',
          'align',
          'formatUL',
          'formatOL',
          'textColor',
          'insertLink',
          'insertImage',
          'insertVideo',
          'emoticons',
          '|',
          'undo',
          'redo',
        ].filter(
          toolbarButton => !excludeToolbarButtons.includes(toolbarButton),
        ),
    tableResizeOffset: 10,
    placeholderText:
      !customFroalaConfig.documentReady && !togglable && `${t('Type here')}...`,
    ...customFroalaConfig,
    events: {
      focus: function () {
        setFroalaCore(this.core)
        setToggled(true)
        setVisible(true)
      },
      blur: (e: any) => {
        const newHtml = e?.target?.innerHTML
        if (input.onBlur) input.onBlur(newHtml)
        if (onBlur && e) onBlur(newHtml)
        if (togglable) setToggled(false)
      },
      init: function () {
        setInitialized(true)

        if (input.value) {
          onModelChange(parseModel(input.value))
        }

        tribute.attach(this.el)
        this.events.on(
          'keydown',
          function (e: any) {
            if (e.which === FroalaEditor?.KEYCODE?.ENTER && tribute.isActive) {
              return false
            }
          },
          true,
        )
      },
      ...(customFroalaConfig?.events ?? {}),
    },
  }

  const onModelChange = (model: string) => {
    if (disabled) {
      return
    }

    if (input.onChange) {
      input.onChange(model)
    }

    if (onChange) {
      if (froalaCore && froalaCore.isEmpty()) {
        onChange(null)
      } else {
        onChange(model)
      }
    }
  }

  if (loading) {
    return <Loader active />
  }

  // To be used in 'tableStyles' in the Froala config above
  const customTableClasses = `
    .no-border thead tr th, .no-border tbody tr td {
      border: none;
    }
  `

  const hasError = touched && error && error.length

  const isSingleLine =
    isBoolean(customFroalaConfig?.multiLine) && !customFroalaConfig?.multiLine

  const model = parseVariables(input.value, variables)

  return (
    <>
      <FroalaGlobalStyle
        toggled={toggled}
        toolbarSticky={froalaConfig.toolbarSticky}
        toolbarStickyOffset={froalaConfig.toolbarStickyOffset}
      />
      <style>{customTableClasses}</style>
      <EditorWrapper
        savingText={`${t('Saving')}...`}
        disabled={disabled}
        toggled={toggled}
        saving={saving}
        error={isSingleLine && hasError}>
        {label}

        {!disabled && (toggled || (sensitive && visible)) ? (
          <FroalaEditorComponent
            key={froalaConfig.toolbarButtons}
            model={initialized ? model : undefined}
            onModelChange={onModelChange}
            config={froalaConfig}
          />
        ) : (
          <DisabledWrapper
            disabled={disabled}
            dangerouslySetInnerHTML={{
              __html: sensitive && !visible ? '**********' : model ?? '',
            }}
            onClick={() => {
              if (!disabled && !disableToggling) {
                setToggled(true)
              }
            }}
          />
        )}
        {sensitive && !toggled && (
          <SensitiveIcon
            name={visible ? 'icon-sensitive-off' : 'icon-sensitive-on'}
            onClick={e => {
              e.stopPropagation()
              setVisible(!visible)
            }}
          />
        )}
        {hasError && <ErrorLabel error={error} />}
      </EditorWrapper>
    </>
  )
}

const SensitiveIcon = styled(Icon).attrs({
  button: true,
  size: 'big',
})`
  &&& {
    position: absolute;
    right: 8px;
    top: 35px;
    cursor: pointer;
    z-index: 1;
    pointer-events: all;
  }
`

/* @ts-ignore */
const DisabledWrapper = styled(Text.P)<DisabledWrapperProps>`
  ${({ theme, disabled }) => css`
    background: ${disabled ? theme.colors.backgroundColor : '#fff'};
    cursor: ${disabled ? 'default' : 'pointer'};
    border: 1px solid #bdcbd0;
    border-color: #bdcbd0;
    box-shadow: 0px 2px 12px -5px rgb(0 46 62 / 5%);
    border-radius: 4px;
    padding: 0.67857143em 1em;
    pointer-events: all;
    min-height: 44px;
  `}
`

const EditorWrapper = styled(({ saving, savingText, error, ...rest }) => (
  <Form.Field {...rest} />
))<EditorWrapperProps>`
  ${({ disabled, error, saving, savingText }) => css`
    pointer-events: ${disabled ? 'none' : 'all'};
    position: relative;

    ${error &&
    css`
      .fr-toolbar,
      .fr-wrapper,
      .second-toolbar {
        border-color: #de5447 !important;
      }

      .fr-wrapper {
        border-bottom-color: rgba(34, 36, 38, 0.15) !important;
      }
    `}

    ${saving &&
    css`
      .fr-toolbar:after {
        content: '${savingText}' !important;
        color: gray !important;
        position: absolute !important;
        top: 32% !important;
        right: 2em !important;
      }
    `};
  `}
`

const Editor = (props: EditorProps) => (
  <Suspense fallback={null}>
    <EditorContent {...props} />
  </Suspense>
)

export default Editor
